import { apiClient } from '../api/apiClient';
import { appConfig } from '../config/appConfig';
import lettersData from '../data/letters.json';
import type { Letter, NewLetter } from '../models/Letter';
import { delay } from '../utils/delay';

export interface LetterService {
  /** The published letters, newest first. Public. */
  getLetters(): Promise<Letter[]>;
  /** Submits a letter, which then waits for an administrator to approve it. */
  addLetter(letter: NewLetter): Promise<Letter>;
}

/**
 * Only the two things anybody may do. Approving, correcting and removing a
 * letter live in adminService beside the same operations on memories, so that
 * everything needing an administrator is reached through one service.
 */

/** Newest first, which is how letters are presented everywhere. */
export function sortByNewestFirst(letters: readonly Letter[]): Letter[] {
  return [...letters].sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
  );
}

/**
 * In-memory implementation used while no backend is configured.
 *
 * A submitted letter shows up immediately here, which the real site does not do
 * -- it would wait for approval. That matches the mock memories beside it, and
 * the form says as much in mock mode rather than pretending otherwise.
 */
export function createMockLetterService(seed: readonly Letter[] = lettersData): LetterService {
  let letters: Letter[] = sortByNewestFirst(seed);

  return {
    async getLetters() {
      await delay(200);
      return [...letters];
    },

    async addLetter(letter: NewLetter) {
      await delay(400);
      const created: Letter = {
        // The real identifier is generated by the Worker, never the browser.
        id: `local-${crypto.randomUUID()}`,
        name: letter.name,
        message: letter.message,
        created: new Date().toISOString(),
      };
      letters = sortByNewestFirst([created, ...letters]);
      return created;
    },
  };
}

export function createHttpLetterService(): LetterService {
  return {
    async getLetters() {
      return sortByNewestFirst(await apiClient.get<Letter[]>('/api/letters'));
    },

    addLetter(letter: NewLetter) {
      return apiClient.post<Letter>('/api/letters', letter);
    },
  };
}

export const letterService: LetterService = appConfig.useMockData
  ? createMockLetterService()
  : createHttpLetterService();
